import { NODE_SERVER_LOCATION } from '../../catch-core/data/constants/default-value.constants';
import { GRID_SPACING } from './map-grid-layout';

interface IPoint {
  x: number;
  y: number;
}

/** A new child lands at a random point within this distance of its parent, on each axis. */
export const CHILD_PLACEMENT_RANGE = 500;
const MAX_ATTEMPTS = 50;

function parseLocation(location: string | null | undefined): IPoint | null {
  const trimmed = typeof location === 'string' ? location.trim() : '';
  if (!trimmed || trimmed === NODE_SERVER_LOCATION) {
    return null;
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 2) {
    return null;
  }
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

/**
 * Picks a random map location within CHILD_PLACEMENT_RANGE (500, 500) of the parent, so a child
 * appears near its parent rather than beneath whatever was placed last on the map. A point that would
 * sit on top of another node is re-rolled; if none is free after a number of tries the last roll is
 * used. Returns `null` when the parent has no position of its own (the map's auto-layout then
 * positions the child), so the caller falls back to the default location.
 */
export function planChildLocation(
  parentLocation: string | null | undefined,
  occupiedLocations: ReadonlyArray<string | null | undefined> | null | undefined,
  random: () => number = Math.random,
): string | null {
  const parent = parseLocation(parentLocation);
  if (!parent) {
    return null;
  }

  const occupied = (occupiedLocations ?? []).map(parseLocation).filter((p): p is IPoint => p !== null);
  const isFree = (slot: IPoint) => !occupied.some((p) => Math.abs(p.x - slot.x) < GRID_SPACING / 2 && Math.abs(p.y - slot.y) < GRID_SPACING / 2);
  const roll = (): IPoint => ({
    x: Math.round(parent.x + (random() * 2 - 1) * CHILD_PLACEMENT_RANGE),
    y: Math.round(parent.y + (random() * 2 - 1) * CHILD_PLACEMENT_RANGE),
  });

  let slot = roll();
  for (let attempt = 1; attempt < MAX_ATTEMPTS && !isFree(slot); attempt++) {
    slot = roll();
  }
  return `${slot.x} ${slot.y}`;
}
